import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Tag,
  Space,
  message,
  Dropdown,
  Menu,
  Upload,
  Spin,
  Tabs,
} from "antd";
import {
  PlusOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, DownOutlined,
  FileDoneOutlined, UploadOutlined, UserOutlined, AuditOutlined,
  LockOutlined, BellOutlined, CheckCircleFilled, EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import apiPath from "../constants/apiPath";
const { Option } = Select;
const { RangePicker } = DatePicker;

// Contract dates are calendar dates anchored to the business's IST day
// boundaries (backend stores startDate as IST-midnight, endDate as IST
// 23:59:59.999 — see TZ=Asia/Kolkata on the server). Reading them back with
// plain moment.utc() only "happens" to work for endDate, since 23:59:59.999
// IST minus 5:30 stays on the same UTC calendar day — startDate's IST
// midnight minus 5:30 rolls back to the previous UTC day instead. Forcing a
// fixed +5:30 offset (India has no DST, so this is always correct) reverses
// either boundary correctly, regardless of the viewer's own browser timezone.
const istMoment = (date) => moment(date).utcOffset(330);

const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "INR", symbol: "₹" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "AED", symbol: "AED " },
  { code: "SAR", symbol: "SAR " },
];
const currencySymbol = (code) =>
  CURRENCIES.find((c) => c.code === code)?.symbol || (code ? code + " " : "$");

// Max paid-window length allowed per payment term. Upfront (and anything
// unmapped) has no cap — the paid period may span the whole contract.
const TERM_MONTHS = { Monthly: 1, Quarterly: 3, "06-monthly": 6 };
const termMonths = (term) => TERM_MONTHS[term] || null;

// ─── Manual Onboarding Modal ──────────────────────────────────────────────────

const TASK_LABELS = {
  pbStrategyDeck: "PB Strategy Deck",
  linkedinAudit: "LinkedIn Audit",
  contentStrategyDeck: "Content Strategy Deck",
};
const TASK_KEYS = ["pbStrategyDeck", "linkedinAudit", "contentStrategyDeck"];

const StatusPill = ({ status }) => {
  const map = {
    completed: { bg: "#e6f4ea", color: "#1a7a3c", label: "Completed" },
    in_progress: { bg: "#e8f4fd", color: "#1565c0", label: "In Progress" },
    not_started: { bg: "#f5f5f5", color: "#888", label: "Not Started" },
    overdue: { bg: "#fdecea", color: "#c62828", label: "Overdue" },
    pending: { bg: "#f5f5f5", color: "#888", label: "Pending" },
  };
  const s = map[status] || map.not_started;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

const AvatarCircle = ({ name, size = 48 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", background: "#185250", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 600, fontSize: Math.round(size * 0.38), flexShrink: 0,
  }}>
    {(name || "?")[0].toUpperCase()}
  </div>
);

const ManualOnboardingModal = ({ clientId, clientName, visible, onClose, onCompleted }) => {
  const [ob, setOb] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [profileLoading, setProfileLoading] = useState(false);
  const [taskRequestLoading, setTaskRequestLoading] = useState({ pbStrategyDeck: false, linkedinAudit: false, contentStrategyDeck: false });
  const [taskFiles, setTaskFiles] = useState({ pbStrategyDeck: null, linkedinAudit: null, contentStrategyDeck: null });
  const [taskLoading, setTaskLoading] = useState({ pbStrategyDeck: false, linkedinAudit: false, contentStrategyDeck: false });
  const [resumeFile, setResumeFile] = useState(null);
  const [questionnaireFile, setQuestionnaireFile] = useState(null);
  const [basicInfo, setBasicInfo] = useState({ fullName: "", companyName: "", designation: "", industry: "", companyWebsite: "" });

  const baseURL = apiPath.baseURL;
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  const fetchOb = async () => {
    if (!clientId) return;
    setFetchLoading(true);
    try {
      const res = await axios.get(`${baseURL}${apiPath.onboarding.status(clientId)}`, { headers: getHeaders() });
      const data = res.data?.data?.onboarding || null;
      setOb(data);
      // Always set (not just when data exists) — otherwise a client with no
      // saved basic info inherits whatever the previously opened client left
      // in this state.
      const d = data?.steps?.basicInfo?.data || {};
      setBasicInfo({
        fullName: d.fullName || "",
        companyName: d.companyName || "",
        designation: d.designation || "",
        industry: d.industry || "",
        companyWebsite: d.companyWebsite || "",
      });
    } catch (e) {
      console.error("Error fetching onboarding:", e);
    }
    setFetchLoading(false);
  };

  useEffect(() => {
    if (visible && clientId) {
      // Clear the previous client's data immediately — fetchOb repopulates
      // async, and until it lands the form must not show stale values.
      setOb(null);
      setBasicInfo({ fullName: "", companyName: "", designation: "", industry: "", companyWebsite: "" });
      fetchOb();
      setActiveTab("profile");
      setResumeFile(null);
      setQuestionnaireFile(null);
      setTaskFiles({ pbStrategyDeck: null, linkedinAudit: null, contentStrategyDeck: null });
      setTaskLoading({ pbStrategyDeck: false, linkedinAudit: false, contentStrategyDeck: false });
      setTaskRequestLoading({ pbStrategyDeck: false, linkedinAudit: false, contentStrategyDeck: false });
    }
  }, [visible, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompleteClient = async () => {
    setProfileLoading(true);
    try {
      const fd = new FormData();
      if (resumeFile) fd.append("resume", resumeFile);
      if (questionnaireFile) fd.append("questionnaire", questionnaireFile);
      if (basicInfo.fullName) fd.append("fullName", basicInfo.fullName);
      if (basicInfo.companyName) fd.append("companyName", basicInfo.companyName);
      if (basicInfo.designation) fd.append("designation", basicInfo.designation);
      if (basicInfo.industry) fd.append("industry", basicInfo.industry);
      if (basicInfo.companyWebsite) fd.append("companyWebsite", basicInfo.companyWebsite);
      const res = await axios.post(`${baseURL}${apiPath.onboarding.manualClient(clientId)}`, fd, { headers: getHeaders() });
      const linkedinPendingResult = res.data?.data?.linkedinPending;
      message.success(linkedinPendingResult
        ? "Client profile saved — client will be notified to connect LinkedIn"
        : "Client profile completed");
      await fetchOb();
      onCompleted?.();
      // Only switch to brand tasks tab if client side is fully done
      if (!linkedinPendingResult) setActiveTab("tasks");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to complete client profile");
    }
    setProfileLoading(false);
  };

  const handleUploadTask = async (taskKey) => {
    const file = taskFiles[taskKey];
    if (!file) { message.warning("Please select a file first"); return; }
    setTaskLoading((prev) => ({ ...prev, [taskKey]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${baseURL}${apiPath.onboarding.manualBrandTask(clientId, taskKey)}`, fd, { headers: getHeaders() });
      message.success(`${TASK_LABELS[taskKey]} uploaded`);
      setTaskFiles((prev) => ({ ...prev, [taskKey]: null }));
      await fetchOb();
      onCompleted?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Upload failed");
    }
    setTaskLoading((prev) => ({ ...prev, [taskKey]: false }));
  };

  const handleRequestTask = async (taskKey) => {
    setTaskRequestLoading((prev) => ({ ...prev, [taskKey]: true }));
    try {
      const res = await axios.post(`${baseURL}${apiPath.onboarding.manualBrandRequest(clientId, taskKey)}`, {}, { headers: getHeaders() });
      message.success(res.data?.message || "Request sent");
      await fetchOb();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to send request");
    }
    setTaskRequestLoading((prev) => ({ ...prev, [taskKey]: false }));
  };

  const clientDone = ob?.onboardingStatus === "completed";
  // Admin already filled steps 2/3/4 but client hasn't connected LinkedIn yet
  const adminFilledProfile =
    ob?.steps?.basicInfo?.status === "completed" &&
    ob?.steps?.resumeUpload?.status === "completed";
  const linkedinPending = adminFilledProfile && ob?.steps?.linkedinLogin?.status !== "completed";
  const completedTaskCount = TASK_KEYS.filter((k) => ob?.brandTasks?.[k]?.status === "completed").length;

  const profileTab = (
    <div style={{ paddingTop: 16 }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
          Resume <span style={{ color: "#aaa", fontWeight: 400 }}>(any format, max 50 MB)</span>
        </div>
        {ob?.steps?.resumeUpload?.fileUrl && !resumeFile && (
          <div style={{ marginBottom: 8 }}>
            <a href={ob.steps.resumeUpload.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#185250" }}>
              <EyeOutlined /> View current resume
            </a>
          </div>
        )}
        <Upload
          beforeUpload={(f) => { setResumeFile(f); return false; }}
          onRemove={() => setResumeFile(null)}
          fileList={resumeFile ? [{ uid: "r", name: resumeFile.name, status: "done" }] : []}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />} size="small">
            {resumeFile ? "Replace" : ob?.steps?.resumeUpload?.fileUrl ? "Replace Resume" : "Upload Resume"}
          </Button>
        </Upload>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
          Questionnaire <span style={{ color: "#aaa", fontWeight: 400 }}>(any format, max 50 MB)</span>
        </div>
        {ob?.steps?.questionnaire?.pdfUrl && !questionnaireFile && (
          <div style={{ marginBottom: 8 }}>
            <a href={ob.steps.questionnaire.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#185250" }}>
              <EyeOutlined /> View current questionnaire
            </a>
          </div>
        )}
        <Upload
          beforeUpload={(f) => { setQuestionnaireFile(f); return false; }}
          onRemove={() => setQuestionnaireFile(null)}
          fileList={questionnaireFile ? [{ uid: "q", name: questionnaireFile.name, status: "done" }] : []}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />} size="small">
            {questionnaireFile ? "Replace" : ob?.steps?.questionnaire?.pdfUrl ? "Replace Questionnaire" : "Upload Questionnaire"}
          </Button>
        </Upload>
      </div>

      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Basic Info</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 20 }}>
        {[
          { key: "fullName", label: "Full Name" },
          { key: "companyName", label: "Company" },
          { key: "designation", label: "Designation" },
          { key: "industry", label: "Industry" },
          { key: "companyWebsite", label: "Website" },
        ].map(({ key, label }) => (
          <div key={key}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{label}</div>
            <Input
              value={basicInfo[key]}
              onChange={(e) => setBasicInfo((prev) => ({ ...prev, [key]: e.target.value }))}
              size="small"
              placeholder={label}
            />
          </div>
        ))}
      </div>

      {clientDone ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#1a7a3c", padding: "10px 14px", background: "#e6f4ea", borderRadius: 8, marginBottom: 12 }}>
          <CheckCircleFilled />
          <span style={{ fontSize: 13 }}>Client profile completed. You can still update files or info above.</span>
        </div>
      ) : linkedinPending ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#1565c0", padding: "10px 14px", background: "#e8f4fd", borderRadius: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16 }}>🔗</span>
          <span style={{ fontSize: 13 }}>Documents saved. Waiting for client to connect their LinkedIn account to complete onboarding.</span>
        </div>
      ) : null}
      <Button
        type="primary"
        style={{ background: "#185250", borderColor: "#185250" }}
        loading={profileLoading}
        onClick={handleCompleteClient}
        block
      >
        {clientDone || linkedinPending ? "Update Client Profile" : "Complete Client Profile"}
      </Button>
    </div>
  );

  const tasksTab = (
    <div style={{ paddingTop: 16 }}>
      {!clientDone && !linkedinPending ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#aaa" }}>
          <LockOutlined style={{ fontSize: 32, marginBottom: 12, display: "block" }} />
          <div style={{ fontWeight: 600, color: "#666" }}>Complete client profile first</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Brand tasks become available after the client profile is marked complete.</div>
        </div>
      ) : (
        <>
          {TASK_KEYS.map((taskKey) => {
            const task = ob?.brandTasks?.[taskKey];
            const isCompleted = task?.status === "completed";
            const taskFile = taskFiles[taskKey];
            const reqLoading = taskRequestLoading[taskKey];

            // Per-task request row
            let requestRow = null;
            if (taskKey === "pbStrategyDeck") {
              requestRow = (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
                  <Button icon={<BellOutlined />} size="small" loading={reqLoading} onClick={() => handleRequestTask("pbStrategyDeck")}>
                    Request to Client Services
                  </Button>
                </div>
              );
            } else if (taskKey === "linkedinAudit") {
              requestRow = (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
                  <Button icon={<BellOutlined />} size="small" loading={reqLoading} onClick={() => handleRequestTask("linkedinAudit")}>
                    Request to Brand Manager
                  </Button>
                </div>
              );
            } else {
              // contentStrategyDeck — auto-routes to the client's assigned
              // Brand Strategist(s), same as linkedinAudit → Brand Manager
              // above. Used to require picking an assignee from this
              // dropdown, which fed an assigneeId the backend demanded —
              // now the backend routes by role automatically, matching how
              // the other two tasks already worked, so this is a plain
              // request button like theirs.
              requestRow = (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
                  <Button icon={<BellOutlined />} size="small" loading={reqLoading} onClick={() => handleRequestTask("contentStrategyDeck")}>
                    Request to Brand Strategist
                  </Button>
                </div>
              );
            }

            return (
              <div key={taskKey} style={{ border: "1px solid #f0f0f0", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{TASK_LABELS[taskKey]}</div>
                  <StatusPill status={task?.status || "not_started"} />
                </div>
                {isCompleted && task.fileUrl && (
                  <div style={{ marginBottom: 10 }}>
                    <a href={task.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#185250" }}>
                      <EyeOutlined /> View uploaded file
                    </a>
                    {task.submittedAt && (
                      <span style={{ fontSize: 11, color: "#aaa", marginLeft: 12 }}>
                        Uploaded {moment(task.submittedAt).format("DD-MMM-YYYY")}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Upload
                    beforeUpload={(f) => { setTaskFiles((prev) => ({ ...prev, [taskKey]: f })); return false; }}
                    onRemove={() => setTaskFiles((prev) => ({ ...prev, [taskKey]: null }))}
                    fileList={taskFile ? [{ uid: taskKey, name: taskFile.name, status: "done" }] : []}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />} size="small">
                      {taskFile ? "Replace" : isCompleted ? "Replace File" : "Select File"}
                    </Button>
                  </Upload>
                  {taskFile && (
                    <Button
                      type="primary"
                      size="small"
                      style={{ background: "#185250", borderColor: "#185250" }}
                      loading={taskLoading[taskKey]}
                      onClick={() => handleUploadTask(taskKey)}
                    >
                      Submit
                    </Button>
                  )}
                </div>
                {requestRow}
              </div>
            );
          })}
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={640}
      styles={{ body: { padding: 0 } }}
      title={null}
      style={{ top: 40 }}
      destroyOnClose
    >
      {fetchLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ padding: "24px 28px 18px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 14 }}>
            <AvatarCircle name={clientName} size={52} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a" }}>{clientName}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Manual Onboarding</div>
            </div>
          </div>

          {/* Status strip */}
          <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
            {[
              { label: "CLIENT", value: <StatusPill status={ob?.onboardingStatus || "not_started"} /> },
              { label: "BRAND TASKS", value: <StatusPill status={ob?.brandOnboardingStatus || "not_started"} /> },
              { label: "PROGRESS", value: <span style={{ fontWeight: 600, fontSize: 13 }}>{completedTaskCount}/{TASK_KEYS.length} done</span> },
            ].map((item, i) => (
              <div key={i} style={{ flex: 1, padding: "12px 18px", borderRight: i < 2 ? "1px solid #f0f0f0" : "none", textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>{item.label}</div>
                {item.value}
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ padding: "0 28px 24px" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="small"
              style={{ marginTop: 4 }}
              items={[
                {
                  key: "profile",
                  label: <span><UserOutlined style={{ marginRight: 6 }} />Client profile</span>,
                  children: profileTab,
                },
                {
                  key: "tasks",
                  label: <span><AuditOutlined style={{ marginRight: 6 }} />Brand tasks {completedTaskCount}/{TASK_KEYS.length}</span>,
                  children: tasksTab,
                },
              ]}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

// ─── Contract Management ──────────────────────────────────────────────────────

const ContractManagement = ({ clientId, clientName, visible, onClose }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [paidModalVisible, setPaidModalVisible] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [manualOnboardingVisible, setManualOnboardingVisible] = useState(false);
  // Anchor date while picking the paid period, used to cap the window length to
  // the selected payment term (Monthly=1mo, Quarterly=3mo, 06-monthly=6mo).
  const [paidTmpStart, setPaidTmpStart] = useState(null);
  const [renewPaidTmpStart, setRenewPaidTmpStart] = useState(null);
  const [form] = Form.useForm();
  const [renewForm] = Form.useForm();
  const [paidForm] = Form.useForm();
  const { request } = useRequest();

  useEffect(() => {
    console.log("ContractManagement useEffect - visible:", visible, "clientId:", clientId);
    if (visible && clientId) {
      fetchContracts();
      fetchClientDetails();
      fetchOnboardingStatus();
    } else if (visible && !clientId) {
      console.error("Modal is visible but clientId is missing!");
    }
  }, [visible, clientId]);

  const fetchOnboardingStatus = () => {
    if (!clientId) return;
    request({
      url: apiPath.onboarding.status(clientId),
      method: "GET",
      onSuccess: (data) => setOnboardingStatus(data?.data?.onboarding || null),
      onError: () => setOnboardingStatus(null),
    });
  };

  const handleStartOnboarding = () => {
    if (!clientId) return;
    setOnboardingLoading(true);
    request({
      url: apiPath.onboarding.start(clientId),
      method: "POST",
      onSuccess: () => {
        ShowToast("Onboarding started successfully", Severty.SUCCESS);
        setOnboardingLoading(false);
        fetchOnboardingStatus();
      },
      onError: (err) => {
        ShowToast(err || "Failed to start onboarding", Severty.ERROR);
        setOnboardingLoading(false);
      },
    });
  };

  const fetchClientDetails = () => {
    request({
      url: `/admin/customer/user-detail/${clientId}`,
      method: "GET",
      onSuccess: (data) => {
        console.log("Client details response:", data);
        // Handle different response structures
        if (data?.data) {
          setClientDetails(data.data);
        } else if (data) {
          setClientDetails(data);
        }
      },
      onError: (error) => {
        console.error("Error fetching client details:", error);
        // Set default values if API fails
        setClientDetails({ name: clientName });
      },
    });
  };

  const fetchContracts = () => {
    if (!clientId) {
      console.error("No clientId provided");
      return;
    }
    setLoading(true);
    request({
      url: `${apiPath.contractByClient}/${clientId}`,
      method: "GET",
      onSuccess: (data) => {
        console.log("Contracts response:", data);
        // Handle different response structures
        let contractsData = [];
        if (data?.data?.contracts) {
          contractsData = data.data.contracts;
        } else if (data?.data?.data?.contracts) {
          contractsData = data.data.data.contracts;
        } else if (Array.isArray(data?.data)) {
          contractsData = data.data;
        } else if (data?.contracts) {
          contractsData = data.contracts;
        } else if (Array.isArray(data)) {
          contractsData = data;
        }

        console.log("Parsed contracts data:", contractsData);
        console.log("Number of contracts:", contractsData.length);
        if (contractsData.length > 0) {
          console.log("First contract sample:", contractsData[0]);
        }
        setContracts(contractsData);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching contracts:", error);
        ShowToast(error?.response?.data?.message || "Failed to fetch contracts", Severty.ERROR);
        setContracts([]);
        setLoading(false);
      },
    });
  };

  const handleCreateContract = (values) => {
    if (!clientId) {
      ShowToast("Client ID is missing", Severty.ERROR);
      return;
    }

    const payload = {
      clientId,
      startDate: values.dateRange[0].format("YYYY-MM-DD"),
      endDate: values.dateRange[1].format("YYYY-MM-DD"),
      amount: parseFloat(values.amount),
      currency: values.currency || "USD",
      paymentTerms: values.paymentTerms,
      paidFrom: values.paidRange?.[0] ? values.paidRange[0].format("YYYY-MM-DD") : null,
      paidUntil: values.paidRange?.[1] ? values.paidRange[1].format("YYYY-MM-DD") : null,
    };

    console.log("Creating contract with payload:", payload);

    request({
      url: apiPath.contractCreate,
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        console.log("Contract created response:", data);
        ShowToast(data?.message || "Contract created successfully", Severty.SUCCESS);
        setModalVisible(false);
        form.resetFields();
        fetchContracts();
      },
      onError: (error) => {
        console.error("Error creating contract:", error);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to create contract", Severty.ERROR);
      },
    });
  };

  const handleRenewContract = (values) => {
    if (!clientId || !selectedContract?._id) {
      ShowToast("Client ID or Contract ID is missing", Severty.ERROR);
      return;
    }

    const payload = {
      clientId,
      parentContractId: selectedContract._id,
      startDate: values.dateRange[0].format("YYYY-MM-DD"),
      endDate: values.dateRange[1].format("YYYY-MM-DD"),
      amount: parseFloat(values.amount),
      currency: values.currency || "USD",
      paymentTerms: values.paymentTerms,
      paidFrom: values.paidRange?.[0] ? values.paidRange[0].format("YYYY-MM-DD") : null,
      paidUntil: values.paidRange?.[1] ? values.paidRange[1].format("YYYY-MM-DD") : null,
    };

    console.log("Renewing contract with payload:", payload);

    request({
      url: apiPath.contractCreate,
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        console.log("Renewal created response:", data);
        ShowToast(data?.message || `Renewal ${selectedContract.renewalNumber + 1} created successfully`, Severty.SUCCESS);
        setRenewModalVisible(false);
        renewForm.resetFields();
        setSelectedContract(null);
        fetchContracts();
      },
      onError: (error) => {
        console.error("Error renewing contract:", error);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to renew contract", Severty.ERROR);
      },
    });
  };

  // Open the Renew Contract modal (creates a new renewal contract)
  const openRenewContract = (record) => {
    const latestContract =
      contracts.length > 0
        ? contracts.reduce(
            (latest, current) =>
              current.renewalNumber > latest.renewalNumber ? current : latest,
            contracts[0]
          )
        : record;
    setSelectedContract(record);
    // .utc() — contract dates are calendar dates anchored to a fixed business
    // day, not viewer-local moments; parsing in local time here would shift
    // the suggested renewal start date depending on the admin's timezone.
    const nextDay = istMoment(latestContract.endDate).add(1, "day");
    renewForm.setFieldsValue({
      dateRange: [nextDay, null], // start prefilled, end for user to pick
    });
    setRenewPaidTmpStart(null);
    setRenewModalVisible(true);
  };

  const openPaidModal = (contract) => {
    setSelectedContract(contract);
    // "Renew Payment" should default to the NEXT period (the day after the
    // currently-recorded paidUntil), not re-show the period that's already
    // on file — pre-filling the same dates made it easy to accidentally
    // resubmit the already-paid window instead of actually extending
    // coverage forward. Only fall back to the current paidFrom/paidUntil
    // when there's no paidUntil yet (first-time "Set Paid Period").
    let defaultStart = contract.paidFrom ? istMoment(contract.paidFrom) : null;
    let defaultEnd = contract.paidUntil ? istMoment(contract.paidUntil) : null;
    if (contract.paidUntil) {
      const nextStart = istMoment(contract.paidUntil).add(1, "day");
      const monthsToAdd =
        contract.paymentTerms === "Quarterly" ? 3 :
        contract.paymentTerms === "06-monthly" ? 6 :
        1; // Monthly, Upfront, and any other cadence default to a 1-month period
      defaultStart = nextStart;
      defaultEnd = istMoment(contract.paidUntil).add(monthsToAdd, "month");
    }
    paidForm.setFieldsValue({
      paidRange: [defaultStart, defaultEnd],
      amount: contract.amount,
      currency: contract.currency || "USD",
    });
    setPaidModalVisible(true);
  };

  const handleSetPaidPeriod = (values) => {
    if (!selectedContract?._id) {
      ShowToast("Contract ID is missing", Severty.ERROR);
      return;
    }
    const range = values.paidRange || [];
    const payload = {
      paidFrom: range[0] ? range[0].format("YYYY-MM-DD") : null,
      paidUntil: range[1] ? range[1].format("YYYY-MM-DD") : null,
      amount:
        values.amount !== undefined && values.amount !== null && values.amount !== ""
          ? parseFloat(values.amount)
          : undefined,
      currency: values.currency || undefined,
    };
    request({
      url: `${apiPath.contractMarkAsPaid}/${selectedContract._id}`,
      method: "PUT",
      data: payload,
      onSuccess: (data) => {
        ShowToast(data?.message || "Paid period updated", Severty.SUCCESS);
        setPaidModalVisible(false);
        paidForm.resetFields();
        setSelectedContract(null);
        fetchContracts();
      },
      onError: (error) => {
        console.error("Error updating paid period:", error);
        ShowToast(error?.response?.data?.message || "Failed to update paid period", Severty.ERROR);
      },
    });
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    form.setFieldsValue({
      dateRange: [istMoment(contract.startDate), istMoment(contract.endDate)],
      amount: contract.amount,
      currency: contract.currency || "USD",
      paymentTerms: contract.paymentTerms,
      paidRange: [
        contract.paidFrom ? istMoment(contract.paidFrom) : null,
        contract.paidUntil ? istMoment(contract.paidUntil) : null,
      ],
    });
    setPaidTmpStart(contract.paidFrom ? istMoment(contract.paidFrom) : null);
    setModalVisible(true);
  };

  const handleExportToExcel = () => {
    if (contracts.length === 0) {
      ShowToast("No contracts to export", Severty.WARNING);
      return;
    }

    // Sort contracts by renewal number
    const sortedContracts = [...contracts].sort((a, b) => a.renewalNumber - b.renewalNumber);
    const originalContract = sortedContracts.find(c => c.renewalNumber === 0);
    const renewals = sortedContracts.filter(c => c.renewalNumber > 0).sort((a, b) => a.renewalNumber - b.renewalNumber);

    // Prepare Excel data structure
    const excelData = [];

    // Header row with section titles
    const headerRow = [
      // DETAILS section
      "S.No",
      "Client Name",
      "Business Development Manager",
      "Brand Manager",
      "Brand Strategist",
      "Company",
      "Designation",
      "Industry",
      "Location",
      // ORIGINAL CONTRACT section
      "Contract Start Date",
      "Contract End Date",
      "Contract Length (months)",
      "Pricing per month",
      "Payment Terms",
      // RENEWAL 1 section
      "Contract Start Date",
      "Contract End Date",
      "Contract Length (months)",
      "Pricing per month",
      "Payment Terms",
      // RENEWAL 2 section
      "Contract Start Date",
      "Contract End Date",
      "Contract Length (months)",
      "Pricing per month",
      "Payment Terms",
      // RENEWAL 3 section
      "Contract Start Date",
      "Contract End Date",
      "Contract Length (months)",
      "Pricing per month",
      "Payment Terms",
    ];
    excelData.push(headerRow);

    // Data row
    const dataRow = [
      // DETAILS
      1, // S.No
      clientName || clientDetails?.name || "N/A",
      "N/A", // Business Development Manager (not available in current data)
      clientDetails?.brandManager?.length > 0
        ? clientDetails.brandManager.map(bm => bm.name).join(", ")
        : "N/A",
      clientDetails?.brandStrategist?.length > 0
        ? clientDetails.brandStrategist.map(bs => bs.name).join(", ")
        : "N/A",
      clientDetails?.company || "N/A",
      clientDetails?.designation || "N/A",
      clientDetails?.industry || "N/A",
      clientDetails?.location || clientDetails?.country || "N/A",
      // ORIGINAL CONTRACT
      originalContract
        ? istMoment(originalContract.startDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      originalContract
        ? istMoment(originalContract.endDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      originalContract?.contractLength || "N/A",
      originalContract ? `${currencySymbol(originalContract.currency)}${parseFloat(originalContract.amount).toFixed(2)}` : "N/A",
      originalContract?.paymentTerms || "N/A",
      // RENEWAL 1
      renewals[0]
        ? istMoment(renewals[0].startDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[0]
        ? istMoment(renewals[0].endDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[0]?.contractLength || "N/A",
      renewals[0] ? `${currencySymbol(renewals[0].currency)}${parseFloat(renewals[0].amount).toFixed(2)}` : "N/A",
      renewals[0]?.paymentTerms || "N/A",
      // RENEWAL 2
      renewals[1]
        ? istMoment(renewals[1].startDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[1]
        ? istMoment(renewals[1].endDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[1]?.contractLength || "N/A",
      renewals[1] ? `${currencySymbol(renewals[1].currency)}${parseFloat(renewals[1].amount).toFixed(2)}` : "N/A",
      renewals[1]?.paymentTerms || "N/A",
      // RENEWAL 3
      renewals[2]
        ? istMoment(renewals[2].startDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[2]
        ? istMoment(renewals[2].endDate).format("DD-MMM-YYYY")
        : "dd-mm-yyyy",
      renewals[2]?.contractLength || "N/A",
      renewals[2] ? `${currencySymbol(renewals[2].currency)}${parseFloat(renewals[2].amount).toFixed(2)}` : "N/A",
      renewals[2]?.paymentTerms || "N/A",
    ];
    excelData.push(dataRow);

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contracts");

    // Set column widths
    const colWidths = [
      { wch: 5 },   // S.No
      { wch: 15 },  // Client Name
      { wch: 25 },  // BD Manager
      { wch: 15 },  // Brand Manager
      { wch: 18 },  // Brand Strategist
      { wch: 15 },  // Company
      { wch: 12 },  // Designation
      { wch: 12 },  // Industry
      { wch: 12 },  // Location
      { wch: 18 },  // Original Start Date
      { wch: 18 },  // Original End Date
      { wch: 20 },  // Original Length
      { wch: 18 },  // Original Amount
      { wch: 15 },  // Original Payment Terms
      { wch: 18 },  // Renewal 1 Start
      { wch: 18 },  // Renewal 1 End
      { wch: 20 },  // Renewal 1 Length
      { wch: 18 },  // Renewal 1 Amount
      { wch: 15 },  // Renewal 1 Payment Terms
      { wch: 18 },  // Renewal 2 Start
      { wch: 18 },  // Renewal 2 End
      { wch: 20 },  // Renewal 2 Length
      { wch: 18 },  // Renewal 2 Amount
      { wch: 15 },  // Renewal 2 Payment Terms
      { wch: 18 },  // Renewal 3 Start
      { wch: 18 },  // Renewal 3 End
      { wch: 20 },  // Renewal 3 Length
      { wch: 18 },  // Renewal 3 Amount
      { wch: 15 },  // Renewal 3 Payment Terms
    ];
    ws['!cols'] = colWidths;

    // ── Second sheet: Payment Records (ledger across all contracts) ──
    const paymentRows = [
      ["Contract", "Paid From", "Paid Until", "Amount (per month)", "Recorded On", "Status"],
    ];
    [...contracts]
      .sort((a, b) => (a.renewalNumber || 0) - (b.renewalNumber || 0))
      .forEach((contract) => {
        const label =
          contract.renewalNumber === 0
            ? "Original Contract"
            : `Renewal ${contract.renewalNumber}`;
        let payments = Array.isArray(contract.payments) ? [...contract.payments] : [];
        // Always include the current paid window if it isn't already logged
        if (contract.paidUntil) {
          const hasCurrent = payments.some(
            (p) =>
              p.paidUntil &&
              istMoment(p.paidUntil).isSame(istMoment(contract.paidUntil), "day")
          );
          if (!hasCurrent) {
            payments.push({
              paidFrom: contract.paidFrom,
              paidUntil: contract.paidUntil,
              amount: contract.amount,
              currency: contract.currency,
              recordedAt: contract.updated_at || contract.created_at,
            });
          }
        }
        payments
          .sort(
            (a, b) =>
              new Date(a.paidFrom || a.recordedAt) - new Date(b.paidFrom || b.recordedAt)
          )
          .forEach((p) => {
            const now = istMoment();
            let status = "Current";
            if (p.paidUntil && now.isAfter(istMoment(p.paidUntil), "day")) status = "Past";
            else if (p.paidFrom && now.isBefore(istMoment(p.paidFrom), "day")) status = "Upcoming";
            paymentRows.push([
              label,
              p.paidFrom ? istMoment(p.paidFrom).format("DD-MMM-YYYY") : "N/A",
              p.paidUntil ? istMoment(p.paidUntil).format("DD-MMM-YYYY") : "N/A",
              `${currencySymbol(p.currency)}${parseFloat(p.amount || 0).toFixed(2)}`,
              p.recordedAt ? moment(p.recordedAt).format("DD-MMM-YYYY") : "N/A",
              status,
            ]);
          });
      });

    const wsPayments = XLSX.utils.aoa_to_sheet(paymentRows);
    wsPayments["!cols"] = [
      { wch: 18 }, // Contract
      { wch: 15 }, // Paid From
      { wch: 15 }, // Paid Until
      { wch: 18 }, // Amount
      { wch: 15 }, // Recorded On
      { wch: 12 }, // Status
    ];
    XLSX.utils.book_append_sheet(wb, wsPayments, "Payment Records");

    // Export to file
    const fileName = `Contract_${clientName || "Client"}_${moment().format("YYYY-MM-DD")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    ShowToast("Contract data exported to Excel successfully", Severty.SUCCESS);
  };

  const handleUpdateContract = (values) => {
    const payload = {
      startDate: values.dateRange[0].format("YYYY-MM-DD"),
      endDate: values.dateRange[1].format("YYYY-MM-DD"),
      amount: parseFloat(values.amount),
      currency: values.currency || "USD",
      paymentTerms: values.paymentTerms,
      paidFrom: values.paidRange?.[0] ? values.paidRange[0].format("YYYY-MM-DD") : null,
      paidUntil: values.paidRange?.[1] ? values.paidRange[1].format("YYYY-MM-DD") : null,
    };

    request({
      url: `${apiPath.contractUpdate}/${selectedContract._id}`,
      method: "PUT",
      data: payload,
      onSuccess: (data) => {
        ShowToast(data?.message || "Contract updated successfully", Severty.SUCCESS);
        setModalVisible(false);
        form.resetFields();
        setSelectedContract(null);
        fetchContracts();
      },
      onError: (error) => {
        console.error("Error updating contract:", error);
        ShowToast(error?.response?.data?.message || "Failed to update contract", Severty.ERROR);
      },
    });
  };

  // Deletes a single mistakenly-recorded payment window from the ledger,
  // without touching the rest of the contract (unlike handleDeleteContract,
  // which removes the whole contract).
  const handleDeletePayment = (contractId, paymentId) => {
    Modal.confirm({
      title: "Delete this payment record?",
      content: "This removes only this paid window from the payment history. This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        request({
          url: apiPath.contractDeletePayment(contractId, paymentId),
          method: "DELETE",
          onSuccess: (data) => {
            ShowToast(data?.message || "Payment record deleted successfully", Severty.SUCCESS);
            fetchContracts();
          },
          onError: (error) => {
            console.error("Error deleting payment record:", error);
            ShowToast(error?.response?.data?.message || "Failed to delete payment record", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleDeleteContract = (contractId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this contract?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        request({
          url: `${apiPath.contractDelete}/${contractId}`,
          method: "DELETE",
          onSuccess: (data) => {
            ShowToast(data?.message || "Contract deleted successfully", Severty.SUCCESS);
            fetchContracts();
          },
          onError: (error) => {
            console.error("Error deleting contract:", error);
            ShowToast(error?.response?.data?.message || "Failed to delete contract", Severty.ERROR);
          },
        });
      },
    });
  };

  // Build the full ordered payment ledger for a contract, including the current
  // paidFrom/paidUntil window if it predates the ledger. Shared by the table
  // (child rows), the Paid Through cell, and the Payment Records modal.
  const buildPayments = (contract) => {
    let payments = Array.isArray(contract.payments) ? [...contract.payments] : [];
    if (contract.paidUntil) {
      const hasCurrent = payments.some(
        (p) =>
          p.paidUntil &&
          istMoment(p.paidUntil).isSame(istMoment(contract.paidUntil), "day") &&
          (!p.paidFrom || !contract.paidFrom
            ? true
            : istMoment(p.paidFrom).isSame(istMoment(contract.paidFrom), "day"))
      );
      if (!hasCurrent) {
        payments.push({
          paidFrom: contract.paidFrom,
          paidUntil: contract.paidUntil,
          amount: contract.amount,
          currency: contract.currency,
          recordedAt: contract.updated_at || contract.created_at,
        });
      }
    }
    payments.sort(
      (a, b) =>
        new Date(a.paidFrom || a.recordedAt) - new Date(b.paidFrom || b.recordedAt)
    );
    return payments;
  };

  // Render a single paid window as a status-colored tag.
  const renderPaidTag = (p, key) => {
    const now = istMoment();
    const from = p.paidFrom ? istMoment(p.paidFrom) : null;
    const until = p.paidUntil ? istMoment(p.paidUntil) : null;
    const fromText = from ? `${from.format("DD-MMM-YYYY")} → ` : "";

    let color = "green";
    let label = " (Paid)";
    if (until && until.isBefore(now, "day")) {
      color = "red";
      label = " (Expired)";
    } else if (from && from.isAfter(now, "day")) {
      color = "gold";
      label = " (Upcoming)";
    }
    return (
      <Tag color={color} key={key} style={{ margin: 0 }}>
        {fromText}
        {until ? until.format("DD-MMM-YYYY") : "—"}
        {label}
      </Tag>
    );
  };

  // One row per paid window: the original paid period, then a new row for each
  // renewed payment. Contract-level cells (type, dates, amount, terms, actions)
  // are merged across a contract's payment rows via rowSpan so they read as a
  // single contract, and only "Paid Through" changes row to row.
  const tableData = [];
  (contracts || []).forEach((contract) => {
    const payments = buildPayments(contract);
    const rows = payments.length > 0 ? payments : [null];
    rows.forEach((p, idx) => {
      tableData.push({
        ...contract,
        _payment: p,
        _payIndex: idx,
        _payCount: rows.length,
        _rowKey: `${contract._id || contract.id}-pay-${idx}`,
      });
    });
  });

  // Merge a contract-level cell across all of that contract's payment rows, and
  // top-align it so it lines up with the first payment row rather than centering.
  const contractCell = (record) => ({
    rowSpan: record._payIndex === 0 ? record._payCount : 0,
    style: { verticalAlign: "top" },
  });

  const columns = [
    {
      title: "Contract Type",
      key: "renewalNumber",
      onCell: contractCell,
      render: (_, record) => {
        if (record.renewalNumber === 0) {
          return <Tag color="blue">Original Contract</Tag>;
        }
        return <Tag color="green">Renewal {record.renewalNumber}</Tag>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      onCell: contractCell,
      render: (date) => {
        if (!date) return "N/A";
        try {
          // .utc() — these are calendar dates, not moments in time. Reading
          // via viewer-local time re-shifts them depending on the browser's
          // timezone (e.g. Hong Kong, UTC+8) even though the server always
          // stores a deterministic UTC timestamp for a given IST calendar day.
          return istMoment(date).format("DD-MMM-YYYY");
        } catch (e) {
          console.error("Error formatting start date:", date, e);
          return date ? String(date) : "N/A";
        }
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      onCell: contractCell,
      render: (date) => {
        if (!date) return "N/A";
        try {
          return istMoment(date).format("DD-MMM-YYYY");
        } catch (e) {
          console.error("Error formatting end date:", date, e);
          return date ? String(date) : "N/A";
        }
      },
    },
    {
      title: "Contract Length",
      dataIndex: "contractLength",
      onCell: contractCell,
      render: (length) => {
        if (length === null || length === undefined) return "N/A";
        return `${length} month${length !== 1 ? "s" : ""}`;
      },
    },
    {
      title: "Amount (per month)",
      dataIndex: "amount",
      // Not merged: each payment row shows the amount recorded for that paid
      // window (falls back to the contract amount for rows without a payment).
      render: (amount, record) => {
        const p = record._payment;
        const value = p && p.amount !== undefined && p.amount !== null ? p.amount : amount;
        const cur = p && p.currency ? p.currency : record.currency;
        if (value === null || value === undefined) return "N/A";
        return `${currencySymbol(cur)}${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      title: "Payment Terms",
      dataIndex: "paymentTerms",
      onCell: contractCell,
    },
    {
      title: "Paid Through",
      key: "paidUntil",
      render: (_, record) => {
        // Each row carries a single paid window; render just that one.
        if (!record._payment) {
          return <Tag>Not Paid</Tag>;
        }
        return renderPaidTag(record._payment, record._rowKey);
      },
    },
    {
      title: "Actions",
      key: "actions",
      // fixed: "right",
      width: 220,
      // Not merged like the other contract-level columns: a contract with more
      // than one payment record needs its own per-row Delete action (for that
      // one payment window), not just the contract-level actions on row 0.
      render: (_, record) => {
        // Extra payment rows (idx > 0) only get a Delete action, scoped to
        // that one ledger entry — everything else stays on row 0.
        if (record._payIndex > 0) {
          if (!record._payment?._id) return null;
          return (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleDeletePayment(record._id, record._payment._id)}
            >
              Delete
            </Button>
          );
        }

        const renewMenu = (
          <Menu>
            {record.renewalNumber === 0 && (
              <Menu.Item
                key="renew-contract"
                onClick={() => openRenewContract(record)}
              >
                Renew Contract
              </Menu.Item>
            )}
            <Menu.Item
              key="renew-payment"
              onClick={() => openPaidModal(record)}
            >
              Renew Payment
            </Menu.Item>
          </Menu>
        );

        // "Delete Payment" clears just that one paid window (backend
        // re-derives paidFrom/paidUntil/isPaid from whatever's left) — it
        // does NOT delete the contract, even when it's the only payment on
        // file. It used to be hidden in that single-payment case on the
        // theory that it was "equivalent to Delete Contract", but that's
        // not true, and hiding it pushed people toward Edit instead — which
        // only clears the contract's own paidFrom/paidUntil fields and
        // leaves this stale payments[] entry behind, producing a contract
        // that shows a "Paid" tag from the ledger while "Set Paid" (gated on
        // paidUntil) reappears at the same time. So: always offer it
        // whenever there's an actual payment record to delete.
        const deleteMenu = (
          <Menu>
            <Menu.Item
              key="delete-contract"
              danger
              onClick={() => handleDeleteContract(record._id)}
            >
              Delete Contract
            </Menu.Item>
            {record._payment?._id && (
              <Menu.Item
                key="delete-payment"
                danger
                onClick={() => handleDeletePayment(record._id, record._payment._id)}
              >
                Delete Payment
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Space size="middle">
            {!record.paidUntil && (
              <Button
                type="link"
                size="small"
                onClick={() => openPaidModal(record)}
              >
                Set Paid
              </Button>
            )}
            <Button
              type="link"
              size="small"
              onClick={() => handleEditContract(record)}
            >
              Edit
            </Button>
            <Dropdown overlay={deleteMenu} trigger={["click"]}>
              <Button type="link" size="small" danger>
                Delete <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown overlay={renewMenu} trigger={["click"]}>
              <Button type="link" size="small">
                Renew <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={`Contract Management - ${clientName || "Client"}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1320}
      style={{ top: 20 }}
    >
      <Card
        className="gfx-toolbar-card"
        extra={
          <Space size="middle" className="gfx-toolbar gfx-toolbar-nowrap">
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => setRecordModalVisible(true)}
              disabled={contracts.length === 0}
            >
              Record
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              disabled={contracts.length === 0}
            >
              Export to Excel
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchContracts}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              className={
                contracts.some((c) => c.renewalNumber === 0) ? "gfx-btn-active" : undefined
              }
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedContract(null);
                form.resetFields();
                setPaidTmpStart(null);
                setModalVisible(true);
              }}
              disabled={contracts.some((c) => c.renewalNumber === 0)}
            >
              {contracts.some((c) => c.renewalNumber === 0)
                ? "Original Contract Exists"
                : "Create Original Contract"}
            </Button>
            {contracts.length > 0 &&
            onboardingStatus?.onboardingStatus === "completed" &&
            onboardingStatus?.brandOnboardingStatus === "completed" ? (
              // Fully done (client + brand) — a single status pill replaces the
              // Start/Manual Onboarding pair, since there's nothing left to do.
              <Button
                type="primary"
                className="gfx-btn-active gfx-btn-success"
                icon={<CheckCircleFilled />}
                disabled
              >
                Onboarding Completed
              </Button>
            ) : (
              <>
                {contracts.length > 0 && (
                  <Button
                    type="primary"
                    className={
                      onboardingStatus?.onboardingStatus === "completed" &&
                      onboardingStatus?.brandOnboardingStatus !== "completed"
                        ? "gfx-btn-active gfx-btn-warning"
                        : "gfx-btn-active"
                    }
                    loading={onboardingLoading}
                    onClick={handleStartOnboarding}
                    disabled={onboardingStatus?.completionMethod === "manual"}
                  >
                    {onboardingStatus?.completionMethod === "manual"
                      ? "Manual Onboarding Active"
                      : onboardingStatus?.onboardingStatus === "in_progress"
                      ? "Onboarding In Progress"
                      : onboardingStatus?.onboardingStatus === "completed" &&
                        onboardingStatus?.brandOnboardingStatus !== "completed"
                      ? "Brand Onboarding Pending"
                      : onboardingStatus?.onboardingStatus === "overdue"
                      ? "Restart Onboarding"
                      : "Start Onboarding"}
                  </Button>
                )}
                {contracts.length > 0 && (
                  <Button
                    type="primary"
                    icon={<FileDoneOutlined />}
                    onClick={() => setManualOnboardingVisible(true)}
                  >
                    Manual Onboarding
                  </Button>
                )}
              </>
            )}
          </Space>
        }
      >
        <Table
          className="gfx-contract-table"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record._rowKey || record._id || record.id || Math.random()}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: "No contracts found. Create your first contract above.",
          }}
        />
      </Card>

      {/* Create/Edit Contract Modal */}
      <Modal
        title={selectedContract ? "Edit Contract" : "Create Original Contract"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedContract(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={selectedContract ? handleUpdateContract : handleCreateContract}
        >
          <Form.Item
            label="Contract Period"
            name="dateRange"
            rules={[{ required: true, message: "Please select contract period" }]}
          >
            <RangePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
          </Form.Item>

          <Form.Item
            label="Amount (per month)"
            name="amount"
            rules={[
              { required: true, message: "Please enter amount" },
              {
                validator: (_, value) => {
                  if (!value && value !== 0) {
                    return Promise.resolve();
                  }
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) {
                    return Promise.reject(new Error("Please enter a valid number"));
                  }
                  if (numValue < 0) {
                    return Promise.reject(new Error("Amount must be positive"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount per month"
              addonBefore={
                <Form.Item name="currency" noStyle initialValue="USD">
                  <Select style={{ width: 90 }}>
                    {CURRENCIES.map((c) => (
                      <Option key={c.code} value={c.code}>
                        {c.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              }
            />
          </Form.Item>

          <Form.Item
            label="Payment Terms"
            name="paymentTerms"
            rules={[{ required: true, message: "Please select payment terms" }]}
          >
            <Select placeholder="Select payment terms">
              <Option value="Upfront">Upfront</Option>
              <Option value="Quarterly">Quarterly</Option>
              <Option value="06-monthly">06-monthly</Option>
              <Option value="Monthly">Monthly</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Paid Period (optional)"
            name="paidRange"
            extra="Must fall within the contract period, and no longer than the payment term (Monthly = 1 month, Quarterly = 3, 06-monthly = 6)."
          >
            <RangePicker
              style={{ width: "100%" }}
              format="DD-MMM-YYYY"
              onCalendarChange={(dates) =>
                setPaidTmpStart(dates ? dates[0] || dates[1] : null)
              }
              disabledDate={(current) => {
                if (!current) return false;
                const range = form.getFieldValue("dateRange");
                if (
                  range &&
                  range[0] &&
                  range[1] &&
                  (current < moment(range[0]).startOf("day") ||
                    current > moment(range[1]).endOf("day"))
                ) {
                  return true;
                }
                // Cap the window length to the selected payment term.
                const months = termMonths(form.getFieldValue("paymentTerms"));
                if (months && paidTmpStart) {
                  const anchor = moment(paidTmpStart);
                  if (
                    current > anchor.clone().add(months, "months") ||
                    current < anchor.clone().subtract(months, "months")
                  ) {
                    return true;
                  }
                }
                return false;
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space size="middle">
              <Button type="primary" htmlType="submit">
                {selectedContract ? "Update" : "Create"} Contract
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setSelectedContract(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Renew Contract Modal */}
      <Modal
        title={`Renew Contract - Renewal ${selectedContract?.renewalNumber + 1 || 1}`}
        open={renewModalVisible}
        onCancel={() => {
          setRenewModalVisible(false);
          renewForm.resetFields();
          setSelectedContract(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={renewForm}
          layout="vertical"
          onFinish={handleRenewContract}
        >
          <Form.Item
            label="Renewal Period"
            name="dateRange"
            rules={[
              { required: true, message: "Please select renewal period" },
              {
                validator: (_, value) => {
                  if (!value || !value[0] || !selectedContract || contracts.length === 0) {
                    return Promise.resolve();
                  }
                  // Find the latest contract (highest renewal number) to get the end date
                  const latestContract = contracts.reduce((latest, current) => {
                    return current.renewalNumber > latest.renewalNumber ? current : latest;
                  }, contracts[0]);

                  const renewalStartDate = moment(value[0]);
                  const previousEndDate = istMoment(latestContract.endDate);

                  if (renewalStartDate.isSameOrBefore(previousEndDate)) {
                    return Promise.reject(
                      new Error(`Renewal must start after ${previousEndDate.format("DD-MMM-YYYY")} (latest contract end date)`)
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              format="DD-MMM-YYYY"
              disabledDate={(current) => {
                if (!selectedContract || contracts.length === 0) return false;
                // Find the latest contract to get the end date
                const latestContract = contracts.reduce((latest, current) => {
                  return current.renewalNumber > latest.renewalNumber ? current : latest;
                }, contracts[0]);
                // Disable dates before or equal to latest contract end date
                return current && current.isSameOrBefore(istMoment(latestContract.endDate), 'day');
              }}
            />
          </Form.Item>

          <Form.Item
            label="Amount (per month)"
            name="amount"
            rules={[
              { required: true, message: "Please enter amount" },
              {
                validator: (_, value) => {
                  if (!value && value !== 0) {
                    return Promise.resolve();
                  }
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) {
                    return Promise.reject(new Error("Please enter a valid number"));
                  }
                  if (numValue < 0) {
                    return Promise.reject(new Error("Amount must be positive"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount per month"
              addonBefore={
                <Form.Item name="currency" noStyle initialValue="USD">
                  <Select style={{ width: 90 }}>
                    {CURRENCIES.map((c) => (
                      <Option key={c.code} value={c.code}>
                        {c.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              }
            />
          </Form.Item>

          <Form.Item
            label="Payment Terms"
            name="paymentTerms"
            rules={[{ required: true, message: "Please select payment terms" }]}
          >
            <Select placeholder="Select payment terms">
              <Option value="Upfront">Upfront</Option>
              <Option value="Quarterly">Quarterly</Option>
              <Option value="06-monthly">06-monthly</Option>
              <Option value="Monthly">Monthly</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Paid Period (optional)"
            name="paidRange"
            extra="Must fall within the renewal period, and no longer than the payment term (Monthly = 1 month, Quarterly = 3, 06-monthly = 6)."
          >
            <RangePicker
              style={{ width: "100%" }}
              format="DD-MMM-YYYY"
              onCalendarChange={(dates) =>
                setRenewPaidTmpStart(dates ? dates[0] || dates[1] : null)
              }
              disabledDate={(current) => {
                if (!current) return false;
                const range = renewForm.getFieldValue("dateRange");
                if (
                  range &&
                  range[0] &&
                  range[1] &&
                  (current < moment(range[0]).startOf("day") ||
                    current > moment(range[1]).endOf("day"))
                ) {
                  return true;
                }
                const months = termMonths(renewForm.getFieldValue("paymentTerms"));
                if (months && renewPaidTmpStart) {
                  const anchor = moment(renewPaidTmpStart);
                  if (
                    current > anchor.clone().add(months, "months") ||
                    current < anchor.clone().subtract(months, "months")
                  ) {
                    return true;
                  }
                }
                return false;
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space size="middle">
              <Button type="primary" htmlType="submit">
                Create Renewal
              </Button>
              <Button
                onClick={() => {
                  setRenewModalVisible(false);
                  renewForm.resetFields();
                  setSelectedContract(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Set Paid Period Modal */}
      <Modal
        title="Set Paid Period"
        open={paidModalVisible}
        onCancel={() => {
          setPaidModalVisible(false);
          paidForm.resetFields();
          setSelectedContract(null);
        }}
        footer={null}
        width={520}
      >
        <Form form={paidForm} layout="vertical" onFinish={handleSetPaidPeriod}>
          <Form.Item
            label="Paid Period"
            name="paidRange"
            rules={[{ required: true, message: "Please select the paid period" }]}
            extra="Must fall within the contract period. The customer keeps access until the 'paid until' date, then is automatically marked unpaid and loses access."
          >
            <RangePicker
              style={{ width: "100%" }}
              format="DD-MMM-YYYY"
              disabledDate={(current) => {
                if (!selectedContract) return false;
                const start = istMoment(selectedContract.startDate).startOf("day");
                const end = istMoment(selectedContract.endDate).endOf("day");
                return current && (current < start || current > end);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Amount (per month)"
            name="amount"
            rules={[
              { required: true, message: "Please enter amount" },
              {
                validator: (_, value) => {
                  if (!value && value !== 0) return Promise.resolve();
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) {
                    return Promise.reject(new Error("Please enter a valid number"));
                  }
                  if (numValue < 0) {
                    return Promise.reject(new Error("Amount must be positive"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra="Amount recorded for this paid period."
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount per month"
              addonBefore={
                <Form.Item name="currency" noStyle initialValue="USD">
                  <Select style={{ width: 90 }}>
                    {CURRENCIES.map((c) => (
                      <Option key={c.code} value={c.code}>
                        {c.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              }
            />
          </Form.Item>

          <Form.Item>
            <Space size="middle">
              <Button type="primary" htmlType="submit">
                Save Paid Period
              </Button>
              <Button
                onClick={() => {
                  setPaidModalVisible(false);
                  paidForm.resetFields();
                  setSelectedContract(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <ManualOnboardingModal
        clientId={clientId}
        clientName={clientName}
        visible={manualOnboardingVisible}
        onClose={() => setManualOnboardingVisible(false)}
        onCompleted={fetchOnboardingStatus}
      />

      {/* Payment Records (ledger) Modal */}
      <Modal
        title={`Payment Records - ${clientName || "Client"}`}
        open={recordModalVisible}
        onCancel={() => setRecordModalVisible(false)}
        footer={null}
        width={920}
        style={{ top: 20 }}
      >
        {[...contracts]
          .sort((a, b) => (a.renewalNumber || 0) - (b.renewalNumber || 0))
          .map((contract) => {
            const label =
              contract.renewalNumber === 0
                ? "Original Contract"
                : `Renewal ${contract.renewalNumber}`;
            const payments = buildPayments(contract);
            return (
              <Card
                key={contract._id}
                size="small"
                className="payment-record-card"
                style={{ marginBottom: 16 }}
                title={
                  <Space wrap>
                    <Tag color={contract.renewalNumber === 0 ? "blue" : "green"}>
                      {label}
                    </Tag>
                    <span>
                      {istMoment(contract.startDate).format("DD-MMM-YYYY")} →{" "}
                      {istMoment(contract.endDate).format("DD-MMM-YYYY")}
                    </span>
                    <span>
                      {currencySymbol(contract.currency)}
                      {parseFloat(contract.amount || 0).toFixed(2)}/mo
                    </span>
                    <span>{contract.paymentTerms}</span>
                  </Space>
                }
              >
                <Table
                  size="small"
                  rowKey={(r, i) => r._id || i}
                  pagination={false}
                  dataSource={payments}
                  locale={{ emptyText: "No payments recorded yet." }}
                  columns={[
                    {
                      title: "Paid Period",
                      key: "period",
                      render: (_, p) =>
                        `${p.paidFrom ? istMoment(p.paidFrom).format("DD-MMM-YYYY") : "—"} → ${p.paidUntil ? istMoment(p.paidUntil).format("DD-MMM-YYYY") : "—"}`,
                    },
                    {
                      title: "Amount (per month)",
                      key: "amount",
                      render: (_, p) =>
                        `${currencySymbol(p.currency)}${parseFloat(p.amount || 0).toFixed(2)}`,
                    },
                    {
                      title: "Recorded On",
                      key: "recordedAt",
                      render: (_, p) =>
                        p.recordedAt ? moment(p.recordedAt).format("DD-MMM-YYYY") : "—",
                    },
                    {
                      title: "Status",
                      key: "status",
                      render: (_, p) => {
                        const now = istMoment();
                        if (p.paidUntil && now.isAfter(istMoment(p.paidUntil), "day"))
                          return <Tag color="red">Past</Tag>;
                        if (p.paidFrom && now.isBefore(istMoment(p.paidFrom), "day"))
                          return <Tag color="gold">Upcoming</Tag>;
                        return <Tag color="green">Current</Tag>;
                      },
                    },
                    {
                      title: "Action",
                      key: "action",
                      render: (_, p) =>
                        p._id ? (
                          <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => handleDeletePayment(contract._id, p._id)}
                          >
                            Delete
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            );
          })}
      </Modal>
    </Modal>
  );
};

export default ContractManagement;
